(function () {
  'use strict';

  const API = 'https://fast.a.klaviyo.com/reviews/api/featured_reviews/';

  // Klaviyo serves review photos through a resizing proxy: the stored path is
  // appended to an origin that takes the dimensions in the URL itself.
  const MEDIA_PROXY = 'https://reviews-media.services.klaviyo.com/abc';
  const MEDIA_ORIGIN = 'https://klaviyo.s3.amazonaws.com/reviews/images/';

  const DRAG_THRESHOLD = 5; // px of movement before a drag suppresses the click
  const RESUME_DELAY = 1200; // ms of stillness before auto scroll picks back up

  class FeaturedReviews extends HTMLElement {
    constructor() {
      super();

      this.viewport = this.querySelector('[data-reviews-viewport]');
      this.track = this.querySelector('[data-reviews-track]');

      this.companyId = this.dataset.companyId;
      this.maxReviews = parseInt(this.dataset.maxReviews, 10) || 24;
      this.requireImage = this.dataset.requireImage === 'true';
      this.minRating = parseInt(this.dataset.minRating, 10) || 1;
      this.speed = parseInt(this.dataset.speed, 10) || 40;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.shuffle = this.dataset.shuffle === 'true';

      this.paused = false;
      this.dragging = false;
      this.frac = 0;
      this.inView = true;

      this.onResize = this.onResize.bind(this);
      this.tick = this.tick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onWheel = this.onWheel.bind(this);
    }

    connectedCallback() {
      if (!this.companyId || !this.track) return;

      // Don't spend a request until the strip is near the viewport; this sits
      // well down the home page.
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
              this.io.disconnect();
              this.load();
            }
          },
          {rootMargin: '400px'}
        );
        this.io.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.visibilityIo) this.visibilityIo.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      window.removeEventListener('resize', this.onResize);
    }

    async load() {
      let reviews = [];
      try {
        const res = await fetch(`${API}?company_id=${encodeURIComponent(this.companyId)}`);
        if (!res.ok) return;
        const data = await res.json();
        reviews = Array.isArray(data.reviews) ? data.reviews : [];
      } catch (e) {
        return; // leave the section collapsed rather than showing a broken band
      }

      let usable = reviews
        .filter((r) => (r.rating || 0) >= this.minRating)
        .filter((r) => (this.requireImage ? Boolean(r.image_uuid) : true));

      // Shuffle before trimming, so which reviews appear varies as well as the
      // order. With more featured than the maximum, every visit is a different
      // selection, and a quiet week for new reviews still looks alive.
      if (this.shuffle) usable = this.shuffled(usable);

      usable = usable.slice(0, this.maxReviews);

      if (!usable.length) return;

      this.reviews = usable;

      // Reveal before rendering: the viewport is display:none until this class
      // lands, and measuring inside it returns zero width, which would leave
      // the strip with nothing to scroll through.
      this.classList.add('is-loaded');
      this.render();

      this.lastWidth = window.innerWidth;
      window.addEventListener('resize', this.onResize);
      this.bindInteraction();
      this.watchVisibility();
      this.start();
    }

    // Fisher-Yates, on a copy so the fetched order stays intact.
    shuffled(list) {
      const out = list.slice();
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
      }
      return out;
    }

    imageUrl(review, size) {
      const first = String(review.image_uuid || '').split(',')[0];
      if (!first) return null;
      return `${MEDIA_PROXY}/width:${size}/height:${size}/resizing_type:fill/plain/${MEDIA_ORIGIN}${first}`;
    }

    stars(rating) {
      const score = Math.max(0, Math.min(5, Math.round(rating || 0)));
      const wrap = document.createElement('div');
      wrap.className = 'featured-review__stars';
      wrap.setAttribute('aria-label', `${score} out of 5 stars`);

      // Always draw all five so the empty ones are visible and colourable,
      // rather than just trimming the string to the score.
      const on = document.createElement('span');
      on.className = 'featured-review__stars--on';
      on.textContent = '★'.repeat(score);

      const off = document.createElement('span');
      off.className = 'featured-review__stars--off';
      off.textContent = '★'.repeat(5 - score);

      wrap.appendChild(on);
      wrap.appendChild(off);
      return wrap;
    }

    card(review) {
      const el = document.createElement('article');
      el.className = 'featured-review';

      const img = this.imageUrl(review, 600);
      if (img) {
        const figure = document.createElement('div');
        figure.className = 'featured-review__media';
        const image = document.createElement('img');
        image.src = img;
        image.loading = 'lazy';
        image.draggable = false;
        image.alt = '';
        figure.appendChild(image);
        el.appendChild(figure);
      }

      const body = document.createElement('div');
      body.className = 'featured-review__body';
      body.appendChild(this.stars(review.rating));

      // smart_quote is Klaviyo's own short pull-quote; much better in a moving
      // strip than the full paragraph, which nobody can read while it scrolls.
      const quote = document.createElement('p');
      quote.className = 'featured-review__quote';
      quote.textContent = review.smart_quote || review.title || review.content || '';
      body.appendChild(quote);

      const who = review.author || '';
      const what = review.product && review.product.name ? review.product.name : '';

      if (who) {
        const meta = document.createElement('p');
        meta.className = 'featured-review__meta';
        meta.textContent = who;
        body.appendChild(meta);
      }

      if (what) {
        const product = document.createElement('p');
        product.className = 'featured-review__product';
        product.textContent = what;
        body.appendChild(product);
      }

      el.appendChild(body);

      const handle = review.product && (review.product.shopify_handle || review.product.handle);
      if (handle) {
        const link = document.createElement('a');
        link.className = 'featured-review__link';
        link.href = `/products/${handle}`;
        link.setAttribute('aria-label', what ? `View ${what}` : 'View product');
        link.addEventListener('click', (e) => {
          if (this.didDrag) e.preventDefault();
        });
        el.appendChild(link);
      }

      return el;
    }

    render() {
      this.track.innerHTML = '';

      this.reviews.forEach((r) => this.track.appendChild(this.card(r)));

      // Repeat the set until it is at least as wide as the viewport, so the
      // strip always fills the screen no matter how few reviews are featured.
      // Each repeat is reshuffled, so with only a handful of reviews the same
      // run of cards doesn't appear twice on screen at once.
      let guard = 0;
      while (this.track.scrollWidth < this.viewport.clientWidth && guard < 12) {
        const fill = this.shuffle ? this.shuffled(this.reviews) : this.reviews;
        fill.forEach((r) => this.track.appendChild(this.card(r)));
        guard += 1;
      }

      // One full duplicate makes the wrap seamless: once scrolled exactly one
      // set across, the copy sits where the original began.
      this.singleWidth = this.track.scrollWidth;
      Array.from(this.track.children).forEach((child) => {
        this.track.appendChild(child.cloneNode(true));
      });
    }

    bindInteraction() {
      if (this.pauseOnHover) {
        this.viewport.addEventListener('mouseenter', () => {
          this.paused = true;
        });
        this.viewport.addEventListener('mouseleave', () => {
          if (!this.dragging) this.paused = false;
        });
      }

      // Drag to scroll. A plain overflow container only scrolls horizontally
      // on desktop with shift+wheel, which is not discoverable.
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('wheel', this.onWheel, {passive: true});

      // Native momentum scrolling can outrun the animation loop and reach the
      // end of the duplicated set. Wrapping here keeps it endless, and lands
      // on identical content so the correction is invisible.
      this.viewport.addEventListener('scroll', () => this.wrap(), {passive: true});
    }

    onPointerDown(event) {
      this.lastPointerType = event.pointerType;

      // Touch already pans this container natively, with momentum. Taking it
      // over from script fights that and makes a swipe feel like it snags, so
      // touch only pauses the automatic movement and lets the browser scroll.
      this.paused = true;

      if (event.pointerType !== 'mouse') {
        this.viewport.addEventListener('pointerup', this.onPointerUp);
        this.viewport.addEventListener('pointercancel', this.onPointerUp);
        return;
      }

      if (event.button !== 0) return;

      this.dragging = true;
      this.didDrag = false;
      this.startX = event.clientX;
      this.startScroll = this.viewport.scrollLeft;
      this.viewport.setPointerCapture(event.pointerId);
      this.viewport.classList.add('is-dragging');

      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      const dx = event.clientX - this.startX;
      if (Math.abs(dx) > DRAG_THRESHOLD) this.didDrag = true;
      this.viewport.scrollLeft = this.startScroll - dx;
      this.wrap();
    }

    onPointerUp() {
      this.dragging = false;
      this.viewport.classList.remove('is-dragging');
      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewport.removeEventListener('pointercancel', this.onPointerUp);
      this.scheduleResume();
      // Let the click handler see the drag, then clear it.
      setTimeout(() => {
        this.didDrag = false;
      }, 0);
    }

    onWheel() {
      this.paused = true;
      this.wrap();
      this.scheduleResume();
    }

    scheduleResume() {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => {
        // :hover can stick after a tap on touch devices, which would leave the
        // strip paused for good. Only let hover hold it for a real mouse.
        const heldByHover =
          this.pauseOnHover && this.lastPointerType === 'mouse' && this.viewport.matches(':hover');

        if (!this.dragging && !heldByHover) this.paused = false;
      }, RESUME_DELAY);
    }

    // Keep the scroll position inside the first copy, so hand scrolling loops
    // as endlessly as the automatic movement does.
    wrap() {
      if (!this.singleWidth) return;
      if (this.viewport.scrollLeft >= this.singleWidth) {
        this.viewport.scrollLeft -= this.singleWidth;
        this.startScroll -= this.singleWidth;
      } else if (this.viewport.scrollLeft <= 0) {
        this.viewport.scrollLeft += this.singleWidth;
        this.startScroll += this.singleWidth;
      }
    }

    watchVisibility() {
      if (!('IntersectionObserver' in window)) return;
      this.visibilityIo = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          this.inView = e.isIntersecting;
        });
      });
      this.visibilityIo.observe(this);
    }

    prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    start() {
      if (this.prefersReducedMotion()) return; // still hand scrollable
      this.last = performance.now();
      this.raf = requestAnimationFrame(this.tick);
    }

    tick(now) {
      const dt = Math.min((now - this.last) / 1000, 0.1); // ignore long tab-away gaps
      this.last = now;

      if (!this.paused && this.inView && this.singleWidth) {
        // scrollLeft truncates, so sub-pixel movement would round to zero and
        // the strip would sit still at slow speeds. Carry the remainder over.
        const advance = this.speed * dt + this.frac;
        const whole = Math.floor(advance);
        this.frac = advance - whole;

        if (whole > 0) {
          this.viewport.scrollLeft += whole;
          this.wrap();
        }
      }

      this.raf = requestAnimationFrame(this.tick);
    }

    onResize() {
      // Mobile browsers fire resize when the address bar hides on scroll, which
      // is a height-only change. Rebuilding on that threw away the scroll
      // position mid-swipe and snapped the strip back to the start.
      if (window.innerWidth === this.lastWidth) return;
      this.lastWidth = window.innerWidth;

      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        // Keep the shopper roughly where they were rather than resetting.
        const ratio = this.singleWidth ? this.viewport.scrollLeft / this.singleWidth : 0;
        this.render();
        this.viewport.scrollLeft = Math.min(this.singleWidth * ratio, this.singleWidth);
      }, 250);
    }
  }

  if (!customElements.get('featured-reviews')) {
    customElements.define('featured-reviews', FeaturedReviews);
  }
})();
